import React, { useState } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import * as XLSX from "xlsx";
import { BASE_URL } from "../../../global";
import axiosWithToken from "../../../utils/components/axiosTokenConfig";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "75%",
  maxHeight: "80vh",
  overflowY: "auto",
  backgroundColor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 3,
};

export default function GaugeExcelUploadModal({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [acceptedRows, setAcceptedRows] = useState([]);
  const [rejectedRows, setRejectedRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setAcceptedRows([]);
    setRejectedRows([]);
  };

  const parseNumber = (val) => {
    if (val === null || val === undefined || val === "" || val === "-") return null;
    if (typeof val === "number") return val;
    const cleaned = String(val).replace(/[^0-9.-]/g, "");
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileReader = new FileReader();

    fileReader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (!rows || rows.length === 0) {
          toast.error("Excel file has no data");
          return;
        }

        let headerRowIndex = -1;
        for (let i = 0; i < Math.min(10, rows.length); i++) {
          const rowStr = (rows[i] || []).join(" ").toLowerCase();
          if (
            rowStr.includes("std") ||
            rowStr.includes("speci") ||
            rowStr.includes("go") ||
            rowStr.includes("nominal") ||
            rowStr.includes("limit") ||
            rowStr.includes("wear") ||
            rowStr.includes("type")
          ) {
            headerRowIndex = i;
            break;
          }
        }

        if (headerRowIndex === -1) headerRowIndex = 0;

        const mainHeaderRow = rows[headerRowIndex] || [];
        const subHeaderRow = rows[headerRowIndex + 1] || [];
        const isSubHeaderRow = subHeaderRow.some((cell) => {
          const s = String(cell || "").toLowerCase();
          return s.includes("max") || s.includes("min") || s.includes("nominal");
        });

        const headers = [];
        let currentCategory = "";
        const maxCols = Math.max(mainHeaderRow.length, subHeaderRow.length);

        for (let idx = 0; idx < maxCols; idx++) {
          const mainVal = String(mainHeaderRow[idx] || "").trim();
          const subVal = isSubHeaderRow ? String(subHeaderRow[idx] || "").trim() : "";

          if (mainVal) {
            currentCategory = mainVal;
          }

          if (isSubHeaderRow) {
            headers.push(`${currentCategory} ${subVal}`.trim());
          } else {
            headers.push(mainVal);
          }
        }

        const dataStartIndex = isSubHeaderRow ? headerRowIndex + 2 : headerRowIndex + 1;
        const colMap = {};

        headers.forEach((h, idx) => {
          if (!h) return;
          const cleanH = String(h)
            .toLowerCase()
            .replace(/[^a-z0-9]/g, " ")
            .replace(/\s+/g, " ")
            .trim();

          if (
            cleanH.includes("wear limit") ||
            cleanH.includes("wearlimit") ||
            (cleanH.includes("wear") && cleanH.includes("limit"))
          ) {
            colMap.wearLimit = idx;
          } else if (cleanH.includes("nogo") || cleanH.includes("no go")) {
            if (cleanH.includes("max")) {
              colMap.noGoMax = idx;
            } else if (cleanH.includes("min")) {
              colMap.noGoMin = idx;
            } else if (cleanH.includes("nominal") || cleanH.includes("size")) {
              colMap.noGoNominalSize = idx;
            }
          } else if (cleanH.includes("go")) {
            if (cleanH.includes("max")) {
              colMap.goMax = idx;
            } else if (cleanH.includes("min")) {
              colMap.goMin = idx;
            } else if (cleanH.includes("nominal") || cleanH.includes("size")) {
              colMap.goNominalSize = idx;
            }
          } else if (cleanH.includes("gauge type") || cleanH.includes("type")) {
            colMap.gaugeType = idx;
          } else if (
            cleanH.includes("specification") ||
            cleanH.includes("spec") ||
            cleanH.includes("std") ||
            cleanH.includes("size")
          ) {
            if (colMap.specification === undefined) {
              colMap.specification = idx;
            }
          }
        });

        const accepted = [];
        const rejected = [];

        for (let i = dataStartIndex; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.length === 0) continue;

          const hasAnyValue = row.some(
            (val) => val !== null && val !== undefined && String(val).trim() !== ""
          );
          if (!hasAnyValue) continue;

          const rawSpec = colMap.specification !== undefined ? row[colMap.specification] : "";
          const specStr = rawSpec !== null && rawSpec !== undefined ? String(rawSpec).trim() : "";

          const rawType = colMap.gaugeType !== undefined ? row[colMap.gaugeType] : "";
          const typeStr = String(rawType || "").trim().toLowerCase();

          let resolvedGaugeType = null;
          if (typeStr.includes("plug")) {
            resolvedGaugeType = 1;
          } else if (typeStr.includes("snap")) {
            resolvedGaugeType = 2;
          } else if (typeStr === "1" || Number(rawType) === 1) {
            resolvedGaugeType = 1;
          } else if (typeStr === "2" || Number(rawType) === 2) {
            resolvedGaugeType = 2;
          }

          const parsedRow = {
            specification: specStr || "-",
            gaugeType: resolvedGaugeType,
            rawGaugeType: String(rawType || "N/A"),
            gaugeTypeLabel:
              resolvedGaugeType === 1
                ? "Plain Plug"
                : resolvedGaugeType === 2
                ? "Snap Gauge"
                : String(rawType || "Invalid"),
            goNominalSize: parseNumber(
              colMap.goNominalSize !== undefined ? row[colMap.goNominalSize] : null
            ),
            goMax: parseNumber(colMap.goMax !== undefined ? row[colMap.goMax] : null),
            goMin: parseNumber(colMap.goMin !== undefined ? row[colMap.goMin] : null),
            noGoNominalSize: parseNumber(
              colMap.noGoNominalSize !== undefined ? row[colMap.noGoNominalSize] : null
            ),
            noGoMax: parseNumber(colMap.noGoMax !== undefined ? row[colMap.noGoMax] : null),
            noGoMin: parseNumber(colMap.noGoMin !== undefined ? row[colMap.noGoMin] : null),
            wearLimit: parseNumber(
              colMap.wearLimit !== undefined ? row[colMap.wearLimit] : null
            ),
          };

          if (!resolvedGaugeType) {
            rejected.push({
              ...parsedRow,
              rejectionReason: `Invalid Gauge Type for Plain Plug / Snap Gauge (found: '${rawType}')`,
            });
          } else if (!specStr || specStr === "-") {
            rejected.push({
              ...parsedRow,
              rejectionReason: "Missing Specification",
            });
          } else {
            accepted.push(parsedRow);
          }
        }

        setAcceptedRows(accepted);
        setRejectedRows(rejected);
      } catch (err) {
        console.error(err);
        toast.error("Invalid Excel file");
      }
    };

    fileReader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const escapeSql = (str) => {
    if (!str) return "";
    return str.replace(/[\0\x08\x09\x1a\n\r"'\\\%]/g, (char) => {
      switch (char) {
        case "\0": return "\\0";
        case "\x08": return "\\b";
        case "\x09": return "\\t";
        case "\x1a": return "\\z";
        case "\n": return "\\n";
        case "\r": return "\\r";
        case "\"":
        case "'":
        case "\\":
        case "%": return "\\" + char;
        default: return char;
      }
    });
  };

  const sanitizeVal = (val) => {
    if (val === "" || val === null || val === undefined || isNaN(val)) return "NULL";
    return parseFloat(val);
  };

  const handleContinue = async () => {
    if (acceptedRows.length === 0) return;
    setSubmitting(true);

    let successCount = 0;

    for (let i = acceptedRows.length - 1; i >= 0; i--) {
      const row = acceptedRows[i];
      const typeVal = row.gaugeType;
      const specVal = escapeSql(row.specification);
      const gNom = sanitizeVal(row.goNominalSize);
      const gMax = sanitizeVal(row.goMax);
      const gMin = sanitizeVal(row.goMin);
      const ngNom = sanitizeVal(row.noGoNominalSize);
      const ngMax = sanitizeVal(row.noGoMax);
      const ngMin = sanitizeVal(row.noGoMin);
      const wLimit = sanitizeVal(row.wearLimit);

      const query = `INSERT INTO plainSnapGaugeSize (type, specification, goNominalSize, goMax, goMin, noGoNominalSize, noGoMax, noGoMin, wearLimit, status) VALUES (${typeVal}, '${specVal}', ${gNom}, ${gMax}, ${gMin}, ${ngNom}, ${ngMax}, ${ngMin}, ${wLimit}, 1)`;

      try {
        await axiosWithToken.post(`${BASE_URL}dynamic`, { query });
        successCount++;
      } catch (err) {
        console.error("Error importing row:", err);
      }
    }

    setSubmitting(false);
    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} record(s)!`);
      handleClose();
      if (onSuccess) onSuccess();
    } else {
      toast.error("Failed to upload specifications.");
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={handleOpen}
      >
        Upload Excel
      </Button>

      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <Typography variant="h6" textAlign="center" mb={2}>
            Upload Plain Plug / Snap Gauge Excel
          </Typography>

          <Typography textAlign="right" mb={2} color="text.secondary">
            Rejected: {rejectedRows.length} | Accepted: {acceptedRows.length}
          </Typography>

          <Button variant="contained" color="primary" component="label" sx={{ mb: 2 }}>
            Select Excel File
            <input
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
            />
          </Button>

          {acceptedRows.length > 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: 260, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Gauge Type</TableCell>
                    <TableCell>Specification</TableCell>
                    <TableCell align="center">GO Nominal</TableCell>
                    <TableCell align="center">GO Max</TableCell>
                    <TableCell align="center">GO Min</TableCell>
                    <TableCell align="center">NOGO Nominal</TableCell>
                    <TableCell align="center">NOGO Max</TableCell>
                    <TableCell align="center">NOGO Min</TableCell>
                    <TableCell align="center">Wear Limit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {acceptedRows.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{row.gaugeTypeLabel}</TableCell>
                      <TableCell>{row.specification}</TableCell>
                      <TableCell align="center">{row.goNominalSize ?? "-"}</TableCell>
                      <TableCell align="center">{row.goMax ?? "-"}</TableCell>
                      <TableCell align="center">{row.goMin ?? "-"}</TableCell>
                      <TableCell align="center">{row.noGoNominalSize ?? "-"}</TableCell>
                      <TableCell align="center">{row.noGoMax ?? "-"}</TableCell>
                      <TableCell align="center">{row.noGoMin ?? "-"}</TableCell>
                      <TableCell align="center">{row.wearLimit ?? "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {rejectedRows.length > 0 && (
            <>
              <Typography fontWeight="bold" color="error" sx={{ mb: 1, mt: 1 }}>
                Rejected Rows ({rejectedRows.length})
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Gauge Type in File</TableCell>
                      <TableCell>Specification</TableCell>
                      <TableCell sx={{ color: "#d32f2f" }}>Rejection Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rejectedRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{row.rawGaugeType}</TableCell>
                        <TableCell>{row.specification}</TableCell>
                        <TableCell sx={{ color: "#d32f2f" }}>{row.rejectionReason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Box mt={3} display="flex" justifyContent="space-between">
            <Button variant="contained" color="error" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!acceptedRows.length || submitting}
              onClick={handleContinue}
            >
              {submitting ? "Uploading..." : "Continue"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
